import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash-es';
import { useStickyHeight } from '@bucketplace/common';

export interface UseActiveTab {
  activeTab?: string;
  onChangeTab: (tabId: string) => void;
}

interface UseActiveTabProps {
  targetAnchorIds: string[];
  scrollRef: MutableRefObject<HTMLElement | null>;
  containerRef: MutableRefObject<HTMLDivElement | null>;
}

const getSelectorId = (id: string): string => {
  return isNaN(Number(id)) ? `#${id}` : `#\\${id}`;
};

/** 프로그래매틱 스크롤이 목표에 도달한 것으로 간주하는 오차 (px) */
const PROGRAMMATIC_SCROLL_TOLERANCE_PX = 4;
/** 프로그래매틱 스크롤이 중단(사용자 개입 등)됐을 때를 대비한 억제 해제 시간 (ms) */
const PROGRAMMATIC_SCROLL_TIMEOUT_MS = 1500;

export const useActiveTab = ({
  targetAnchorIds,
  scrollRef,
  containerRef,
}: UseActiveTabProps): UseActiveTab => {
  const [activeTab, setActiveTab] = useState<string | undefined>(targetAnchorIds[0]);
  const outerStickyData = useStickyHeight(containerRef);
  const beforeScroll = useRef(0);
  const programmaticScroll = useRef<{ targetY: number, startedAt: number } | null>(null);

  /**
   * 각 ancorId 별로 탭이 활성화 되는 시작 위치를 찾는 함수
   */
  const getScrollData = useCallback(() => {
    if (targetAnchorIds.length === 0) {
      return {};
    }

    const stickyHeight = document.querySelector(getSelectorId(targetAnchorIds[0] ?? ''))?.parentElement?.offsetHeight ?? 0;
    const scrollData: { [key: string]: number } = {};
    const tabItemElements: HTMLElement[] = Array.from(
      document.querySelectorAll(targetAnchorIds.map((id) => getSelectorId(id)).join(', ')),
    );
    for (const tabItemElement of tabItemElements) {
      const absoluteTop = Math.round(window.pageYOffset + tabItemElement.getBoundingClientRect().top);
      const elementTop = scrollRef.current ? tabItemElement.offsetTop : absoluteTop;
      scrollData[tabItemElement.id] = elementTop - stickyHeight - outerStickyData.current;
    }

    return scrollData;
  }, [outerStickyData, scrollRef, targetAnchorIds]);

  /**
   * 스크롤 이벤트가 발생할 때마다 호출.
   * 현재 스크롤의 위치와 getScrollData를 통해 받은 시작 위치가 동일한 경우 탭을 활성화 시킴
   */
  const throttledScroll = useMemo(() => throttle(
    (): void => {
      const scrollY = Math.round(scrollRef.current?.scrollTop ?? window.scrollY);
      if (beforeScroll.current === scrollY) {
        return;
      }

      /**
       * 탭 클릭으로 인한 smooth 스크롤 이동 중에는 중간 섹션들이 순서대로 활성화되지 않도록
       * 스크롤 기반 activeTab 갱신을 억제한다. (클릭 시 이미 activeTab을 고정)
       */
      const programmatic = programmaticScroll.current;
      if (programmatic != null) {
        const reached = Math.abs(scrollY - programmatic.targetY) <= PROGRAMMATIC_SCROLL_TOLERANCE_PX;
        const timedOut = Date.now() - programmatic.startedAt > PROGRAMMATIC_SCROLL_TIMEOUT_MS;
        if (reached || timedOut) {
          programmaticScroll.current = null;
        }
        beforeScroll.current = scrollY;
        return;
      }

      const scrollData = getScrollData();

      const scrollTargets = targetAnchorIds.reduce((acc, id) => {
        const scrollPosition = scrollData[id];
        if (scrollPosition == null) {
          return acc;
        }
        return acc.concat([[scrollPosition, id]]);
      }, [] as [number, string][]);

      const { current, min, max } = outerStickyData;
      const direction = beforeScroll.current < scrollY ? 'down' : 'up';
      const hideOuterSticky = direction === 'up' && current !== max;
      const scroll = hideOuterSticky ? scrollY + (max - min) : scrollY;
      const target = scrollTargets.slice().reverse().find((e) => e[0] <= scroll) ?? scrollTargets[0];

      beforeScroll.current = scrollY;
      setActiveTab(target?.[1]);
    }, 300), [getScrollData, outerStickyData, scrollRef, targetAnchorIds],
  );

  /**
   * onChangeTab 이벤트를 호출했을 때 실행되며, 주로 탭을 클릭했을 때 실행됨.
   * getScrollData를 통해 선택한 탭의 시작 위치로 스크롤 이동.
   *
   * iOS(WebKit)는 스크롤을 비동기로 처리해서, 즉시 스크롤로 한 번도 그려진 적 없는
   * 영역으로 점프하면 메인 스레드 페인트가 따라잡을 때까지 스티키 탭바가 사라진
   * 프레임이 노출된다(깜빡임). 이를 피하기 위해 smooth 스크롤로 이동하고,
   * 이동 중에는 activeTab을 클릭한 탭으로 고정한다. (COMMWEB-7917)
   * @param tabId 선택한 탭의 id
   */
  const handleChangeTab = useCallback((tabId: string): void => {
    const scrollY = Math.round(scrollRef.current?.scrollTop ?? window.scrollY);
    const scrollElement = scrollRef.current ?? window;
    const scrollData = getScrollData();

    const scrollPosition = scrollData[tabId];
    if (scrollPosition == null) {
      return;
    }

    const { current, min, max } = outerStickyData;
    const direction = scrollY - scrollPosition - current > 0 ? 'up' : 'down';

    let diff = 0;
    if (direction === 'up' && current !== max) {
      diff = -(max - min);
    } else if (direction === 'down' && current === max) {
      diff = max - min;
    }

    const targetY = scrollPosition + diff;
    programmaticScroll.current = { targetY, startedAt: Date.now() };
    setActiveTab(tabId);
    scrollElement?.scrollTo({ top: targetY, behavior: 'smooth' });
  }, [getScrollData, outerStickyData, scrollRef]);

  useEffect(() => {
    const scrollElement = scrollRef.current ?? window;
    scrollElement.addEventListener('scroll', throttledScroll, { passive: true });
    return () => {
      scrollElement.removeEventListener('scroll', throttledScroll);
    };
  }, [scrollRef, throttledScroll]);

  return {
    activeTab,
    onChangeTab: handleChangeTab,
  };
};
